import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle, Package, ArrowRight } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CheckoutSuccessPage() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("loading"); // loading, success, error
  const [paymentData, setPaymentData] = useState(null);
  const sessionId = searchParams.get("session_id");

  useEffect(() => {
    const pollPaymentStatus = async (attempts = 0) => {
      const maxAttempts = 5;
      const pollInterval = 2000;

      if (!sessionId) {
        setStatus("error");
        return;
      }

      if (attempts >= maxAttempts) {
        setStatus("error");
        return;
      }

      try {
        const response = await axios.get(`${API}/payments/status/${sessionId}`);
        
        if (response.data.payment_status === "paid") {
          setPaymentData(response.data);
          setStatus("success");
          return;
        } else if (response.data.status === "expired") {
          setStatus("error");
          return;
        }

        // Continue polling
        setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
      } catch (err) {
        console.error("Error checking payment:", err);
        if (attempts < maxAttempts - 1) {
          setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
        } else {
          setStatus("error");
        }
      }
    };

    pollPaymentStatus();
  }, [sessionId]);

  if (status === "loading") {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center" data-testid="checkout-loading">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-black border-t-accent-pink animate-spin mx-auto mb-6"></div>
          <p className="font-heading text-2xl">VERIFICA PAGAMENTO...</p>
        </div>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center" data-testid="checkout-error">
        <div className="text-center max-w-md">
          <div className="w-20 h-20 mx-auto mb-6 border-4 border-red-600 bg-red-100 flex items-center justify-center">
            <span className="text-4xl">!</span>
          </div>
          <h1 className="font-heading text-4xl mb-4">OOPS!</h1>
          <p className="font-body text-lg text-gray-600 mb-8">
            Si è verificato un problema con il pagamento. Controlla il tuo profilo per lo stato dell'ordine.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/profilo" className="btn-brutal inline-flex items-center justify-center">
              VAI AL PROFILO
            </Link>
            <Link to="/" className="btn-brutal-outline inline-flex items-center justify-center">
              TORNA ALLA HOME
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="checkout-success">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center"
        >
          {/* Success Icon */}
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", delay: 0.2 }}
            className="w-24 h-24 mx-auto mb-8 border-4 border-black bg-accent-green flex items-center justify-center"
          >
            <CheckCircle size={48} strokeWidth={2.5} />
          </motion.div>

          <h1 className="font-heading text-5xl sm:text-6xl mb-4">GRAZIE!</h1>
          <p className="font-body text-xl text-gray-600 mb-8">
            Il tuo ordine è stato confermato. Riceverai presto un'email con i dettagli.
          </p>

          {/* Order Summary Box */}
          <div className="border-4 border-black p-8 mb-8 text-left">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-12 bg-accent-yellow border-2 border-black flex items-center justify-center">
                <Package size={24} />
              </div>
              <div>
                <h2 className="font-heading text-xl">ORDINE CONFERMATO</h2>
                <p className="font-mono text-sm text-gray-500">
                  Pagamento: €{paymentData?.amount_total ? (paymentData.amount_total / 100).toFixed(2) : "0.00"}
                </p>
              </div>
            </div>

            <div className="space-y-4 border-t-2 border-black pt-6">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-black text-white flex items-center justify-center font-mono text-sm">1</div>
                <p className="font-body">Ordine ricevuto e confermato</p>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-gray-200 text-gray-500 flex items-center justify-center font-mono text-sm">2</div>
                <p className="font-body text-gray-500">In preparazione</p>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-gray-200 text-gray-500 flex items-center justify-center font-mono text-sm">3</div>
                <p className="font-body text-gray-500">Spedito</p>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-gray-200 text-gray-500 flex items-center justify-center font-mono text-sm">4</div>
                <p className="font-body text-gray-500">Consegnato</p>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/profilo" 
              className="btn-brutal inline-flex items-center justify-center gap-2"
              data-testid="go-to-profile"
            >
              VEDI I TUOI ORDINI
              <ArrowRight size={20} />
            </Link>
            <Link 
              to="/abbigliamento" 
              className="btn-brutal-outline inline-flex items-center justify-center"
              data-testid="continue-shopping"
            >
              CONTINUA LO SHOPPING
            </Link>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
